import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useSyncOrganizationInvoiceBoostsMutation, TotadminPermission } from 'data/__generated__';

// Le fichier de boost de facturation (Google Sheets) porte une ligne par colonne microstore, avec le
// pourcentage de bonus appliqué aux transactions facturées (conso et redevance). Cette popup est le SEUL
// chemin qui remplit ce fichier : sans elle, une organisation ou une colonne créée depuis la dernière
// synchro n'a pas de ligne, donc pas de bonus possible.
export const SyncInvoiceBoostsPopup = ({
    isOpen,
    setIsOpen,
}: {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
}) => {
    const [syncOrganizationInvoiceBoosts, { loading, data, reset }] = useSyncOrganizationInvoiceBoostsMutation();

    const result = data?.syncOrganizationInvoiceBoosts;
    const hasSynced = Boolean(result?.success);

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);
        if (!open) reset();
    };

    const onSync = async () => {
        try {
            const { data: syncData } = await syncOrganizationInvoiceBoosts();
            const payload = syncData?.syncOrganizationInvoiceBoosts;

            if (!payload?.success) {
                toast.error(`La synchronisation a échoué : ${payload?.error || 'erreur inconnue'}`);
                return;
            }

            toast.success(
                `Fichier de boost synchronisé : ${payload.addedCount} ligne(s) ajoutée(s), ${payload.updatedCount} mise(s) à jour`,
            );
        } catch (error) {
            toast.error(
                `Impossible de synchroniser le fichier de boost : ${error instanceof Error ? error.message : error}`,
            );
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[560px]">
                <DialogHeader>
                    <DialogTitle>Synchroniser le fichier de boost</DialogTitle>
                    <DialogDescription>
                        Écrit une ligne par colonne microstore de toutes les organisations, tous sites et tous états
                        confondus (archivées comprises).
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                    <p>
                        La colonne <span className="font-medium text-foreground">bonus</span> n'est jamais réécrite :
                        les valeurs déjà saisies restent en place. Les lignes dont la colonne n'existe plus en base sont
                        conservées, à supprimer à la main si besoin.
                    </p>
                    {hasSynced && result ? (
                        <ul
                            className="flex flex-col gap-1 rounded-lg border border-border p-3 text-foreground"
                            data-test="sync-invoice-boosts-summary"
                        >
                            <li>{result.columnCount} colonne(s) microstore en base</li>
                            <li>{result.addedCount} ligne(s) ajoutée(s)</li>
                            <li>{result.updatedCount} ligne(s) mise(s) à jour</li>
                            <li>{result.obsoleteCount} ligne(s) sans colonne correspondante (conservée(s))</li>
                            <li>{result.duplicateCount} id(s) en double à nettoyer dans le fichier</li>
                        </ul>
                    ) : null}
                </div>
                <DialogFooter>
                    {hasSynced && result ? (
                        <>
                            <Button asChild variant="outline">
                                <a href={result.spreadsheetUrl} target="_blank" rel="noreferrer">
                                    Ouvrir le fichier
                                </a>
                            </Button>
                            <Button onClick={() => handleOpenChange(false)}>Fermer</Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => handleOpenChange(false)}>
                                Annuler
                            </Button>
                            <Button
                                onClick={onSync}
                                disabled={loading}
                                data-test="sync-invoice-boosts-confirm"
                                requiredPermissions={[TotadminPermission.InvoicesManage]}
                            >
                                {loading ? 'Synchronisation…' : 'Synchroniser'}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
